import React from 'react'

type ClickAwayMouseEvents = 'onClick' | 'onMouseDown' | 'onMouseUp' | 'onPointerDown' | 'onPointerUp'

type ClickAwayTouchEvents = 'onTouchStart' | 'onTouchEnd'

type ClickAwayEvents = 'click' | 'mousedown' | 'mouseup' | 'touchstart' | 'touchend' | 'pointerdown' | 'pointerup'

function mapEventPropToEvent(eventProp: ClickAwayMouseEvents | ClickAwayTouchEvents) {
  return eventProp.substring(2).toLowerCase() as ClickAwayEvents
}

export interface ClickAwayListenerProps {
  children: React.ReactElement<
    { ref: React.Ref<Element> } & Pick<React.DOMAttributes<Element>, ClickAwayMouseEvents | ClickAwayTouchEvents>
  >
  onClickAway: (event: MouseEvent | TouchEvent) => void
  mouseEvent?: 'onClick' | 'onMouseDown' | 'onMouseUp' | false
  touchEvent?: 'onTouchStart' | 'onTouchEnd' | false
}

export function ClickAwayListener({
  children,
  onClickAway,
  mouseEvent = 'onClick',
  touchEvent = 'onTouchEnd',
}: ClickAwayListenerProps) {
  const nodeRef = React.useRef<Nullable<HTMLElement>>(null)

  const handleClickAway = React.useCallback(
    (event: MouseEvent | TouchEvent) => {
      const node = nodeRef.current
      if (!node) return

      // Ignore events on the component itself or its children
      if (event.target instanceof Node && node.contains(event.target)) {
        return
      }

      onClickAway(event)
    },
    [onClickAway]
  )

  React.useEffect(() => {
    if (mouseEvent) {
      const mappedMouseEvent = mapEventPropToEvent(mouseEvent)

      document.addEventListener(mappedMouseEvent, handleClickAway, true)

      return () => {
        document.removeEventListener(mappedMouseEvent, handleClickAway, true)
      }
    }
  }, [handleClickAway, mouseEvent])

  React.useEffect(() => {
    if (touchEvent) {
      const mappedTouchEvent = mapEventPropToEvent(touchEvent)

      document.addEventListener(mappedTouchEvent, handleClickAway, true)

      return () => {
        document.removeEventListener(mappedTouchEvent, handleClickAway, true)
      }
    }
  }, [handleClickAway, touchEvent])

  // Ensure only one child and forward ref
  return React.cloneElement(children, { ref: nodeRef })
}
